import React, { useEffect, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { ArrowLeftIcon, ArrowRightIcon, AxeIcon, RecycleIcon, StampIcon, XIcon } from 'lucide-react';
import { GalleryImageFrame } from './GalleryImageFrame';
type GalleryPhoto = {
  src: string;
  alt: string;
  label: string;
  detail: string;
  position?: string;
};
const BUILD_PRINCIPLES = [{
  icon: RecycleIcon,
  title: 'Reclaimed oak',
  detail: 'We rescue retired wine barrels and give the oak a second life.'
}, {
  icon: AxeIcon,
  title: 'Built by hand',
  detail: 'Cut, banded, and finished one at a time in our Santa Barbara shop.'
}, {
  icon: StampIcon,
  title: 'Branded to order',
  detail: 'Custom stamps, hardware, and finishes to fit your space or event.'
}];
const PHOTOS: GalleryPhoto[] = [{
  src: 'https://cdn.magicpatterns.com/uploads/gP9NWLa1jB1Dh5fBNXW2Av/IMG_6591.jpg',
  alt: 'Hand lifting the rope pull on a sliding oak barrel lid, revealing the liner beneath',
  label: 'The sliding lid',
  detail: 'A forged horseshoe latch and rope pull make the opening feel as good as it looks.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dTdHhkEwQtqXGs7qvvHtL2/IMG_5900.jpg',
  alt: 'Close-up of the oak bung branded with the word TRASH',
  label: 'Burned-in branding',
  detail: 'A small branded bung adds a bit of character to the reclaimed oak.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/2yMXkRJHdLcr9S8Gg45E5N/Screenshot_2026-07-18_at_12.30.09_AM.png',
  alt: 'Close-up of a metal vent set into the richly grained barrel oak',
  label: 'Thoughtful airflow',
  detail: 'A discreet vent helps the liner work better without interrupting the barrel’s finish.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/4xRQ4yvL81Gd3Z2CiRob57/Screenshot_2026-07-18_at_12.29.52_AM.png',
  alt: 'Close-up of the braided rope pull and dark metal hardware on the barrel lid',
  label: 'Rope & steel',
  detail: 'Braided rope, dark hardware, and weathered oak — chosen for texture as much as function.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/dJMrUAV1ESfoTMmdYu6zPW/6-min.jpg',
  alt: 'Maker lowering a removable metal liner into a handcrafted oak barrel trash can',
  label: 'Easy-change liner',
  detail: 'A durable removable liner drops neatly into place, ready for everyday use and quick cleanup.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/bYMZsJ46MdPVzeeoycnJ6a/IMG_6628_2.jpg',
  alt: 'Barrel trash can with the lid removed and resting against the base, showing the full metal liner',
  label: 'Removable lid',
  detail: 'The entire lid lifts off smoothly for quick access when it\'s time to change the bag.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/f28NYVYiiBGUcyYhwmuYDY/IMG_5921.jpg',
  alt: 'Barrel trash can with the hinged flip lid opened, revealing the liner inside',
  label: 'Hidden liner',
  detail: 'The hinged flip top opens smoothly, keeping the liner tucked away and easy to change.'
}, {
  src: 'https://cdn.magicpatterns.com/uploads/rcjTCjBfbWtQVyn6Z4aQB3/IMG_5980.jpg',
  alt: 'Top view of a closed barrel trash can with horseshoe handle and rope pull',
  label: 'Horseshoe hardware',
  detail: 'A real horseshoe becomes the handle — a rugged nod to the western spirit behind each build.'
}];
export function Gallery() {
  const galleryRef = useRef<HTMLDivElement>(null);
  const closeButtonRef = useRef<HTMLButtonElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const [selectedPhoto, setSelectedPhoto] = useState<GalleryPhoto | null>(null);
  const scrollGallery = (direction: 'back' | 'forward') => {
    const gallery = galleryRef.current;
    if (!gallery) return;
    gallery.scrollBy({
      left: direction === 'forward' ? gallery.clientWidth * 0.78 : -gallery.clientWidth * 0.78,
      behavior: 'smooth'
    });
  };
  const openPhoto = (photo: GalleryPhoto, trigger: HTMLButtonElement) => {
    triggerRef.current = trigger;
    setSelectedPhoto(photo);
  };
  const closePhoto = () => {
    setSelectedPhoto(null);
  };
  useEffect(() => {
    if (!selectedPhoto) return;
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closePhoto();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKeyDown);
    window.setTimeout(() => closeButtonRef.current?.focus(), 0);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKeyDown);
      triggerRef.current?.focus();
    };
  }, [selectedPhoto]);
  return <section id="gallery" className="overflow-hidden bg-oak-dark py-16 sm:py-24" data-id="element-37" data-mp="qfm8is22rmgz">
      <div className="mx-auto max-w-6xl px-4 sm:px-6" data-id="element-38" data-mp="3g67r9in4quf3">
        <div className="flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between" data-id="element-39" data-mp="3b6d33muqoixq">
          <div className="max-w-2xl" data-id="element-40" data-mp="1qoav9hppxq54">
            <span className="font-stamp text-xs uppercase tracking-[0.25em] text-oak-light" data-id="element-41" data-mp="1qpdkzmbb1mjg">
              Our Barrels
            </span>
            <h2 className="mt-3 font-display text-3xl text-cream sm:text-4xl" data-id="element-42" data-mp="1vnnkg942b9av">
              The details do the talking.
            </h2>
            <p className="mt-3 font-body text-parchment/75" data-id="element-43" data-mp="24fh444ew104o">
              Rope handles, real horseshoe pulls, hidden liners, and a branded bung. Every piece is built to be used and noticed.
            </p>
          </div>

          <div className="hidden items-center gap-2 sm:flex" aria-label="Gallery controls" data-id="element-44" data-mp="2ecu4bzgle6y9">
            <button type="button" onClick={() => scrollGallery('back')} className="inline-flex h-11 w-11 items-center justify-center rounded-sm border-2 border-oak-light/60 text-oak-light transition-colors hover:border-cream hover:bg-cream hover:text-oak-dark focus:outline-none focus:ring-2 focus:ring-cream focus:ring-offset-2 focus:ring-offset-oak-dark" aria-label="Show previous barrel details" data-id="element-45" data-mp="2jcq940x462ev">
              <ArrowLeftIcon className="h-5 w-5" aria-hidden="true" data-id="element-46" data-mp="1rcd534obiomd" />
            </button>
            <button type="button" onClick={() => scrollGallery('forward')} className="inline-flex h-11 w-11 items-center justify-center rounded-sm border-2 border-oak-light/60 text-oak-light transition-colors hover:border-cream hover:bg-cream hover:text-oak-dark focus:outline-none focus:ring-2 focus:ring-cream focus:ring-offset-2 focus:ring-offset-oak-dark" aria-label="Show more barrel details" data-id="element-47" data-mp="1repqxsx75j4p">
              <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-48" data-mp="2ozlrd1w581pf" />
            </button>
          </div>
        </div>

        <div ref={galleryRef} className="hide-scrollbar mt-9 flex snap-x snap-mandatory gap-4 overflow-x-auto pb-4" aria-label="Barrel craftsmanship photo gallery" data-id="element-49" data-mp="2k354eb0ojtdx">
          {PHOTOS.map((photo, index) => <motion.figure key={photo.src} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-40px'
        }} transition={{
          duration: 0.45,
          delay: Math.min(index * 0.05, 0.25)
        }} className="w-[84%] shrink-0 snap-start overflow-hidden rounded-sm border-2 border-oak-light/40 bg-bark shadow-lg sm:w-[54%] lg:w-[38%]" data-id="element-50" data-mp="3cn8uvm0u2mee" data-mpi={index}>
              <button type="button" onClick={event => openPhoto(photo, event.currentTarget)} className="group relative block w-full overflow-hidden text-left focus:outline-none focus-visible:ring-2 focus-visible:ring-cream focus-visible:ring-inset" aria-haspopup="dialog" aria-label={`View full-size photo: ${photo.label}`} data-id="element-51" data-mp="2tv7q631do1x2">
                <GalleryImageFrame src={photo.src} alt={photo.alt} className={photo.position} data-id="element-52" data-mp="2fxrjcqf6664s" />
                <span className="absolute inset-x-0 bottom-0 flex translate-y-full items-center justify-between bg-oak-dark/90 px-4 py-3 font-body text-sm font-semibold text-cream transition-transform duration-300 group-hover:translate-y-0 group-focus-visible:translate-y-0" data-id="element-53" data-mp="2vd6m0jqirkhx">
                  View full size
                  <span aria-hidden="true" data-id="element-54" data-mp="2hhultwtlh1tm">↗</span>
                </span>
              </button>
              <figcaption className="border-t border-oak-light/35 px-5 py-4" data-id="element-55" data-mp="2ci87tl5xzhjr">
                <span className="font-body text-base font-semibold text-cream" data-id="element-56" data-mp="27odqgodt7enu">{photo.label}</span>
                <p className="mt-1 font-body text-sm leading-relaxed text-parchment/70" data-id="element-57" data-mp="22qvcgjkxq98z">{photo.detail}</p>
              </figcaption>
            </motion.figure>)}
        </div>

        <p className="mt-1 font-body text-sm text-parchment/60 sm:hidden" data-id="element-58" data-mp="1tpe09canerpk">
          Swipe to explore every detail. Tap a photo for a full-size view.
        </p>

        <div className="mt-10 grid grid-cols-1 gap-5 border-t border-oak-light/35 pt-8 sm:grid-cols-3" data-id="element-59" data-mp="49bh02ecu98l">
          {BUILD_PRINCIPLES.map(principle => {
          const Icon = principle.icon;
          return <article key={principle.title} className="flex gap-4" data-id="element-60" data-mp="dajdqg18320k">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full border border-oak-light/60 text-oak-light" data-id="element-61" data-mp="i8b3b3eeac1l">
                  <Icon className="h-5 w-5" aria-hidden="true" data-id="element-62" data-mp="3hp9es9hf4jgx" />
                </span>
                <div data-id="element-63" data-mp="3lqbcglvosngs">
                  <h3 className="font-body text-base font-semibold text-cream" data-id="element-64" data-mp="3lpjkdl1at7v9">{principle.title}</h3>
                  <p className="mt-1 font-body text-sm leading-relaxed text-parchment/70" data-id="element-65" data-mp="3qn1w1j6pa16y">{principle.detail}</p>
                </div>
              </article>;
        })}
        </div>
      </div>

      <AnimatePresence data-id="element-66" data-mp="2xxmk8r77o49z">
        {selectedPhoto && <motion.div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/85 p-4 sm:p-8" initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onMouseDown={event => {
        if (event.target === event.currentTarget) closePhoto();
      }} data-id="element-67" data-mp="32y0gzd3d3rsp">
            <motion.div role="dialog" aria-modal="true" aria-labelledby="photo-viewer-title" className="relative flex max-h-full w-full max-w-6xl flex-col rounded-sm bg-oak-dark p-2 shadow-2xl sm:p-3" initial={{
          opacity: 0,
          scale: 0.97,
          y: 12
        }} animate={{
          opacity: 1,
          scale: 1,
          y: 0
        }} exit={{
          opacity: 0,
          scale: 0.97,
          y: 12
        }} transition={{
          duration: 0.2,
          ease: 'easeOut'
        }} onKeyDown={event => {
          if (event.key === 'Tab') {
            event.preventDefault();
            closeButtonRef.current?.focus();
          }
        }} data-id="element-68" data-mp="16ncjqvnbv6ue">
              <div className="relative min-h-0 flex-1 overflow-hidden bg-black" data-id="element-69" data-mp="3jtsp7ffiyty1">
                <img src={selectedPhoto.src} alt={selectedPhoto.alt} className="max-h-[calc(100vh-9rem)] w-full object-contain" data-id="element-70" data-mp="3euzn0j5oupd6" />
                <button ref={closeButtonRef} type="button" onClick={closePhoto} className="absolute right-3 top-3 inline-flex h-11 w-11 items-center justify-center rounded-sm border-2 border-cream/80 bg-oak-dark/90 text-cream transition-colors hover:bg-cream hover:text-oak-dark focus:outline-none focus:ring-2 focus:ring-cream focus:ring-offset-2 focus:ring-offset-oak-dark" aria-label="Close full-size photo" data-id="element-71" data-mp="34wtmo5zj5raf">
                  <XIcon className="h-5 w-5" aria-hidden="true" data-id="element-72" data-mp="2h7qvo7j0am39" />
                </button>
              </div>
              <div className="flex items-center justify-between gap-4 px-3 pb-1 pt-3 sm:px-4" data-id="element-73" data-mp="2d5dp5gwfqk5f">
                <p id="photo-viewer-title" className="font-body text-sm font-semibold text-cream sm:text-base" data-id="element-74" data-mp="22k0iruk9nabz">
                  {selectedPhoto.label}
                </p>
                <p className="hidden font-body text-xs text-parchment/65 sm:block" data-id="element-75" data-mp="1tju07u9pnk5y">Press Escape to close</p>
              </div>
            </motion.div>
          </motion.div>}
      </AnimatePresence>
    </section>;
}